import { call, put, select, takeLatest } from 'redux-saga/effects';
import { toast } from 'sonner';
import {
  fetchMetricsSuccess,
  fetchReviewsByCleanerId,
  fetchReviewsSuccess,
  insertNewReview,
  setHasPendingReviews,
  toggleLoader,
} from './reducer';
import { REVIEWS_ACTIONS, REVIEWS_LOADERS } from './utils';
import { InsertReviewDto } from '@/api/requests/DTOS';
import { insertReview } from '@/api/requests/endpoints/POST/inserNewReview';
import { getMetrics } from '@/api/requests/endpoints/GET/getMetrics';
import { ReviewMetrics } from './model';
import { sagaNavigator } from '@/utils/navigator';
import { APP_ROUTES } from '@/utils/appRoutes';
import { To } from 'react-router-dom';
import { fetchCleanerReviews } from '@/api/requests/endpoints/GET/fetchCleanerReviews';
import { selectMetricsById } from './selectors';
import { getUnreviewedBookingsCheck } from '@/api/requests/endpoints/GET/getUnreviewedBookingsCheck';

function* getCleanerReviewsSaga({
  payload: cleanerId,
}: ReturnType<typeof fetchReviewsByCleanerId>) {
  try {
    yield put(toggleLoader({ key: REVIEWS_LOADERS.GET_REVIEWS, value: true }));
    const { error, data = [] } = yield call(fetchCleanerReviews, cleanerId);
    if (error) throw new Error('Something went wrong');
    yield put(fetchReviewsSuccess(data));
  } catch (e) {
    toast.error('Si è verificato un errore', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
      description: 'Non siamo riusciti a recuperare le recensioni',
    });
  } finally {
    yield put(toggleLoader({ key: REVIEWS_LOADERS.GET_REVIEWS, value: false }));
  }
}

function* insertReviewSaga({ payload }: ReturnType<typeof insertNewReview>) {
  const { description, rating, metrics, bookingId } = payload;

  try {
    yield put(
      toggleLoader({ key: REVIEWS_LOADERS.INSERT_NEW_REVIEW, value: true }),
    );

    const metricsById = yield select(selectMetricsById);

    const newReview: InsertReviewDto = {
      description,
      rating,
      metrics: Object.entries(metrics).reduce(
        (acc, [metricId, value]) => ({
          ...acc,
          [metricId]: { ...metricsById[metricId], value },
        }),
        {},
      ),
      bookingId,
    };

    const res = yield insertReview(newReview);
    if (res.error) {
      throw new Error('Something went wrong');
    }
    yield sagaNavigator(APP_ROUTES.REVIEW_CONFIRMATION);
  } catch (e) {
    toast.error('Si è verificato un errore', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
      description:
        'Si è verificato un errore durante il salavataggio. Riprova.',
    });
  } finally {
    yield put(
      toggleLoader({ key: REVIEWS_LOADERS.INSERT_NEW_REVIEW, value: false }),
    );
  }
}

function* fetchMetricsSaga() {
  try {
    const data: ReviewMetrics = yield getMetrics();
    yield put(fetchMetricsSuccess(data));
  } catch (error) {
    console.error('error fetching metrics', error);
    toast.error('Si è verificato un errore', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
      description: 'Riprova',
    });
    yield sagaNavigator(-1 as To);
  }
}

function* checkPendingReviewsSaga() {
  try {
    const res = yield getUnreviewedBookingsCheck();
    if (typeof res === 'boolean') yield put(setHasPendingReviews(res));
  } catch (e) {
    console.log(e);
  }
}

export function* reviewsSaga() {
  yield takeLatest(REVIEWS_ACTIONS.GET_REVIEWS, getCleanerReviewsSaga);
  yield takeLatest(REVIEWS_ACTIONS.INSERT_NEW_REVIEW, insertReviewSaga);
  yield takeLatest(REVIEWS_ACTIONS.FECTH_METRICS, fetchMetricsSaga);
  yield takeLatest(
    REVIEWS_ACTIONS.CHECK_PENDING_REVIEWS,
    checkPendingReviewsSaga,
  );
}
